import {
  actionCardCommands,
  actionCardFailureMessage,
  teammateMessageActionCardKind,
} from '@littlebird/action-cards';
import { useStores, type ActionCard } from '@littlebird/sdk';
import { useState } from 'react';
import { toast } from 'sonner';

import { saveCardEdits } from '@/features/action-cards-view/cardPendingEdits';
import { useWikiPeopleOfUsers } from '@/features/people-wiki/use-wiki-person-of-user';

import {
  messageGroupOf,
  sendableDrafts,
} from './hivemind-conversations-status';
import { saveText, typedKeyOf, typedText } from './hivemind-typed-text';

// What a send says when another device changed the card first. The card refreshes by itself, so it is no reason to show
const CARD_CHANGED = 'This card changed — refreshing.';

const reasonOf = (outcome: Parameters<typeof actionCardFailureMessage>[0]) => {
  const reason = actionCardFailureMessage(outcome);
  return reason === CARD_CHANGED ? undefined : reason;
};

export function useHivemindSendAll(
  chatId: string,
  only?: readonly ActionCard[]
) {
  'use no memo';
  const { actionCards } = useStores();
  const wikiPersonOf = useWikiPeopleOfUsers();
  const [isSending, setIsSending] = useState(false);
  const [refusals, setRefusals] = useState<Record<string, string>>({});
  const chatCards = actionCards.getByChatId(chatId);
  const drafts = only ?? sendableDrafts(chatCards);

  // What the user sees is what goes. Each card's pending edit saves first, so its send carries the revision that
  // holds the edit, and a card with nothing to save goes at the revision it had when the button was pressed. Either
  // way a card another device changed in the meantime is refused, never sent as the replacement the user did not
  // approve. A card whose edit did not save stays a draft, and a refused send does too. Each reason is kept per card
  // until the user acts, and toasted once, however many cards share it. It resolves to whether every edit saved
  const sendAll = async () => {
    setIsSending(true);
    setRefusals({});
    try {
      const saved = await Promise.all(
        drafts.map(async (card) => {
          const { syncId } = card;
          // A save that cannot reach the server fails this card alone
          const failed = { saved: false, revision: null };
          // The editor of a card the list has unmounted has no saver, so its edit is read from what was typed
          const mounted = await saveCardEdits(card.id).catch(() => failed);
          const [first] = messageGroupOf(card, chatCards);
          const typedKey = first && typedKeyOf(first);
          const typed = typedKey ? typedText.get(typedKey) : undefined;
          const edits =
            mounted.saved && mounted.revision === null && typed !== undefined
              ? await saveText(card.id, typed).catch(() => failed)
              : mounted;
          return {
            card,
            typedKey,
            saved: edits.saved,
            syncId: edits.revision ?? syncId,
          };
        })
      );
      // Once every edit is saved the drafts hold the typed text, so the cache is done. One that did not save keeps it
      if (saved.every(({ saved }) => saved))
        saved.forEach(({ typedKey }) => typedKey && typedText.delete(typedKey));
      const refused: Record<string, string> = {};
      for (const { card } of saved.filter(({ saved }) => !saved)) {
        const teammate = teammateMessageActionCardKind.decode(
          card.content
        )?.teammate;
        const name = teammate
          ? (wikiPersonOf(teammate.id)?.name ?? teammate.name)
          : 'this person';
        refused[card.id] =
          `Couldn't save your edit to the message for ${name}, so it was not sent.`;
        toast.error(refused[card.id]);
      }
      const sent = saved.filter(({ saved }) => saved);
      const outcomes = await Promise.all(
        sent.map(({ card, syncId }) =>
          actionCardCommands.confirm(card.id, undefined, syncId)
        )
      );
      sent.forEach(({ card }, index) => {
        const reason = reasonOf(outcomes[index]);
        if (reason) refused[card.id] = reason;
      });
      for (const reason of new Set(outcomes.map(reasonOf)))
        if (reason) toast.error(reason);
      setRefusals(refused);
      return saved.every(({ saved }) => saved);
    } finally {
      setIsSending(false);
    }
  };

  return {
    draftCount: drafts.length,
    isSending,
    sendAll,
    refusals,
    clearRefusals: () => setRefusals({}),
  };
}
